import CloseIcon from "@mui/icons-material/Close";
import { Divider, Grid } from "@mui/material";
import { Box } from "@mui/system";
import { cloneDeep } from "lodash";
import React from "react";
import { ReactElement, useContext, useEffect, useRef, useState } from "react";

import IconRightArrow from "../../../../assets/images/icons/icon_right_arrow.svg";
import HorizontalBox from "../../../../components/Boxes/HorizontalBox/HorizontalBox";
import LateralPanelBox from "../../../../components/Boxes/LateralPanelBox/LateralPanelBox";
import Button from "../../../../components/Buttons/Button/Button";
import ImgIcon from "../../../../components/ImgIcon/ImgIcon";
import FullScreenModal from "../../../../components/Modal/FullScreenModal/FullScreenModal";
import ProgressPanel from "../../../../components/Progress/ProgressPanel/ProgressPanel";
import TabsPanel from "../../../../components/TabsPanel/TabsPanel";
import { AppContext } from "../../../../core/context/appContextProvider";
import useRole from "../../../../core/routing/useRole";
import {
  approvePendingChanges,
  checkInvestorNameAvailability,
  declinePendingChanges,
  saveInvestor,
} from "../../../../services/investor.service";
import {
  addressSchema,
  bankInfoSchema,
  defaultBankInfo,
  defaultInvestorResource,
  defaultMailingAddress,
  defaultTaxAddress,
  defaultWireInfo,
  investorSchema,
  wireInfoSchema,
} from "../../../../utils/constants/form.constants";
import {
  CHANGES_APPROVED_SUCCESS_MESSAGE,
  CHANGES_DECLINE_SUCCESS_MESSAGE,
  ERROR_TAKEN_INVESTOR_NAME,
  GENERIC_ERROR_MESSAGE,
  INVESTOR_TYPE_ENTITY,
  INVESTOR_TYPE_INDIVIDUAL,
  INVESTORS_NO_FUNDS_ERROR,
  TEXT_DISCARD_CHANGES,
  TEXT_SAVE_CHANGES,
  UNSAVED_CHANGES_SUBTITLE,
} from "../../../../utils/constants/text.constants";
import { useForm } from "../../../../utils/hooks/useForm.hook";
import { WireInfo } from "../../../../utils/types/investor.type";
import { ScopeRole } from "../../../../utils/types/user.type";
import InvestorDetails from "../../investorDetails/InvestorDetails";
import InvestorDetailsReadOnlyView from "../../investorDetails/InvestorDetailsReadOnlyView";
import InvestorFunds from "../../investorFunds/InvestorFunds";
import InvestorFundsReadOnly from "../../investorFunds/InvestorFundsReadOnly";
import InvestorWire from "../../investorWire/InvestorWire";
import InvestorWireReadOnly from "../../investorWire/InvestorWireReadOnly";
import { SAVE_INVESTOR_ERROR, SAVE_INVESTOR_SUCCESS } from "../InvestorList.constants";
import InvestorPendingView from "../InvestorPendingView/InvestorPendingView";
import {
  useInvestorEffect,
  useInvestorValidator,
  useTabs,
} from "./InvestorDetailPanel.hooks";
import {
  InvestorDetailPanel,
  StyledBackdrop,
  Title,
} from "./InvestorDetailPanel.styles";

type Anchor = "right";

type Props = {
  showPanel: boolean;
  setShowPanel: any;
  selectedInvestor: string;
  setSelectedInvestor: any;
  isSubmittingInvestor: boolean;
  setSubmitingInvestor: any;
  selectedInvestorListItem: any;
  setSelectedInvestorListItem: any;
};

const IndividualPanel: React.FC<Props> = (props: Props): ReactElement => {
  const {
    showPanel,
    setShowPanel,
    selectedInvestor,
    setSelectedInvestor,
    isSubmittingInvestor,
    setSubmitingInvestor,
    selectedInvestorListItem,
    setSelectedInvestorListItem,
  } = props;

  const { informationAlert, setAppHasChanges } = useContext(AppContext);
  const {
    title,
    investor,
    setInvestor,
    originalName,
    isLoadingInvestor,
    isNewInvestor,
    isCheckingUniqueName,
    setCheckingUniqueName,
    isPanelVisible,
    setPanelVisible,
    originalInvestorObj,
  } = useInvestorEffect(selectedInvestor, showPanel, informationAlert);

  const { hasRole: isClientPortalUser } = useRole([ScopeRole.BASIC_USER]);

  const { hasRole: isSuperAdminOrClientAdmin } = useRole([
    ScopeRole.SUPER_ADMIN,
    ScopeRole.ARK_CLIENT_ADMIN,
    ScopeRole.BASIC_ADMIN,
  ]);

  const toggleDrawer = (anchor: Anchor, open: boolean) => {
    setShowPanel(open);
    setPanelVisible({
      ...isPanelVisible,
      [anchor]: open,
    });
  };

  const {
    currentTab,
    detailsRef,
    fundsRef,
    wiringRef,
    pageBottomRef,
    handleTabChange,
    scrollToBottom
  } = useTabs();

  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUniqueNameError, setUniqueNameError] = useState(false);

  const defaultReceivingBankInfo = investor?.pendingWireInfo?.receivingBankInfo
    ? investor?.pendingWireInfo?.receivingBankInfo
    : investor.wireInfo!.receivingBankInfo!;
  const beneficiaryBankInfo = investor?.pendingWireInfo?.beneficiaryBankInfo
    ? investor?.pendingWireInfo?.beneficiaryBankInfo
    : investor?.wireInfo?.beneficiaryBankInfo!;
  const intermediaryBankInfo = investor?.pendingWireInfo?.intermediaryBankInfo
    ? investor?.pendingWireInfo?.intermediaryBankInfo
    : investor?.wireInfo?.intermediaryBankInfo!;

  const {
    useInput: useInputInvestorDetail,
    useCheckbox: useCheckboxInvestorDetail,
    values: valuesInvestorDetail,
    setValues: setValuesInvestorDetail,
    isDirty: isInvestorDetailDirty,
  } = useForm(investor, investorSchema, false);

  const {
    useInput: useInputAddress,
    useInputSelect: useAddressInputSelect,
    values: addressValues,
    setValues: setAddressValues,
    isDirty: isMalingAddressDirty,
  } = useForm(investor.mailingAddress!, addressSchema, false);

  const {
    useInput: useInputTaxAddress,
    useInputSelect: useInputTaxAddressSelect,
    values: taxAddressValues,
    setValues: setTaxAddressValues,
    isDirty: isTaxAddressDirty,
  } = useForm(investor.taxAddress!, addressSchema, false);

  const {
    useInput: useInputWireInfo,
    values: wireInfoValues,
    setValues: setWireInfoValues,
    isDirty: isWireInfoDirty,
  } = useForm(
    investor.pendingWireInfo! ? investor.pendingWireInfo : investor.wireInfo!,
    wireInfoSchema,
    false
  );

  const {
    useInput: useReceivingInput,
    values: receivingValues,
    setValues: setReceivingValues,
    isDirty: isReceivingDirty,
  } = useForm(defaultReceivingBankInfo, bankInfoSchema, false);

  const {
    useInput: useBeneficiaryInput,
    values: beneficiaryValues,
    setValues: setBeneficiaryValues,
    isDirty: isBeneficiaryInfoDirty,
  } = useForm(beneficiaryBankInfo, bankInfoSchema, false);

  const { isFormValid, setRequiredfieldUpdated } =
    useInvestorValidator(valuesInvestorDetail);

  const {
    useInput: useIntermediaryInput,
    values: intermediaryValues,
    setValues: setIntermediaryValues,
    isDirty: isIntermediaryDirty,
  } = useForm(intermediaryBankInfo, bankInfoSchema, false);

  const hidePanel = () => {
    const defaultValues = {
      ...cloneDeep(defaultInvestorResource),
    };

    setValuesInvestorDetail(defaultValues);
    valuesInvestorDetail.funds.length = 0;
    valuesInvestorDetail.wireInfo = defaultWireInfo;
    setRequiredfieldUpdated(true);
    setTaxAddressValues(defaultValues.taxAddress || defaultTaxAddress);
    setAddressValues(defaultValues.mailingAddress || defaultMailingAddress);
    setReceivingValues(defaultBankInfo);
    setBeneficiaryValues(defaultBankInfo);
    setIntermediaryValues(defaultBankInfo);
    setSelectedInvestor("");
    setSelectedInvestorListItem();
    setInvestor(defaultValues);
    setUniqueNameError(false);
    setWireInfoValues(defaultWireInfo);
    toggleDrawer("right", false);
    setAppHasChanges(false);
  };

  const handleOnClose = () => {
    if (isDirty) setShowLeaveConfirmation(true);
    else {
      setSelectedInvestor("");
      hidePanel();
    }
  };

  const handleOnDiscard = () => {
    hidePanel();
    setSelectedInvestor("");
    setShowLeaveConfirmation(false);
  };

  const handleOnSave = async () => {
    if (
      valuesInvestorDetail.entityType === INVESTOR_TYPE_ENTITY &&
      !valuesInvestorDetail.name
    ) {
      return;
    } else if (
      valuesInvestorDetail.entityType === INVESTOR_TYPE_INDIVIDUAL &&
      (!valuesInvestorDetail.firstName || !valuesInvestorDetail.lastName)
    ) {
      return;
    } else if (
      (valuesInvestorDetail.disregardedEntity &&
        (!valuesInvestorDetail.ownerName || !valuesInvestorDetail.ownerSSN)) ||
      valuesInvestorDetail.funds.length === 0
    ) {
      return;
    }

    if (!valuesInvestorDetail.id) {
      valuesInvestorDetail.canEditWireInfo = false;
      valuesInvestorDetail.canReadWireInfo = false;
      valuesInvestorDetail.className = "investor";
      valuesInvestorDetail.clientId = "";
      valuesInvestorDetail.email = "";
      valuesInvestorDetail.fax = "";
      valuesInvestorDetail.isPrimary = false;
      valuesInvestorDetail.joinedQuarter = "";
      valuesInvestorDetail.legalType = {
        id: "B",
        label: "",
      };
      valuesInvestorDetail.pendingWireInfo = null;
      valuesInvestorDetail.status = "ACTIVE";
      valuesInvestorDetail.wireInfo = wireInfoValues;

      if (valuesInvestorDetail.funds.length === 0) {
        informationAlert(INVESTORS_NO_FUNDS_ERROR, "error");
        return;
      }
    }

    setShowLeaveConfirmation(false);
    let investorToSave = createSavingInvestorObject();

    let fullName = '';

    if (investorToSave.entityType === 'INDIVIDUAL') {
      fullName = investorToSave.middle
        ? `${investorToSave.firstName} ${investorToSave.middle} ${investorToSave.lastName}`
        : `${investorToSave.firstName} ${investorToSave.lastName}`;

    } else if (investorToSave.entityType === 'ENTITY' && investorToSave.name) {
      fullName = investorToSave.name;
    }

    if ((fullName && originalName !== fullName && valuesInvestorDetail.id) || (fullName && isNewInvestor)) {
      setCheckingUniqueName(true);

      try {
        const isNameAvailable = await checkInvestorNameAvailability(fullName);

        if (!isNameAvailable) {
          informationAlert(ERROR_TAKEN_INVESTOR_NAME, "error");
          setUniqueNameError(true);
          return;
        }
      } catch (exception) {
        informationAlert(GENERIC_ERROR_MESSAGE, "error");
        return;
      } finally {
        setSubmitingInvestor(false);
        setCheckingUniqueName(false);
      }
    }

    setSubmitingInvestor(true);
    try {
      if (
        investorToSave.wireInfo &&
        investorToSave.wireInfo.beneficiaryBankInfo
      ) {
        deleteEmptyProps(investorToSave.wireInfo.beneficiaryBankInfo);
      }
      if (investorToSave.wireInfo && investorToSave.wireInfo.beneficiaryAccount)
        deleteEmptyProps(investorToSave.wireInfo.beneficiaryAccount);
      if (
        investorToSave.wireInfo &&
        investorToSave.wireInfo.intermediaryBankInfo
      )
        deleteEmptyProps(investorToSave.wireInfo.intermediaryBankInfo);
      if (investorToSave.wireInfo && investorToSave.wireInfo.receivingBankInfo)
        deleteEmptyProps(investorToSave.wireInfo.receivingBankInfo);

      if (isClientPortalUser) {
        investorToSave.wireInfoClient = investorToSave.clientPortalWireInfo;

        investorToSave = {
          ...investorToSave,
          wireInfo: { ...(originalInvestorObj?.wireInfo as WireInfo) },
          pendingWireInfo: {
            ...(originalInvestorObj?.pendingWireInfo as WireInfo),
          },
        };
      }

      await saveInvestor(investorToSave);
      hidePanel();
      informationAlert(SAVE_INVESTOR_SUCCESS, "success");
    } catch (exception) {
      informationAlert(SAVE_INVESTOR_ERROR, "error");
    } finally {
      setSubmitingInvestor(false);
      setCheckingUniqueName(false);
    }
  };

  const deleteEmptyProps = (obj: any): any => {
    return Object.keys(obj).forEach((k) => {
      if (
        !obj[k] ||
        obj[k] === undefined ||
        (Array.isArray(obj[k]) && obj[k].length === 0)
      ) {
        delete obj[k];
      }
    });
  };

  const createSavingInvestorObject = () => {
    valuesInvestorDetail.mailingAddress = addressValues;
    valuesInvestorDetail.taxAddress = taxAddressValues;
    const wireInfoData = { ...cloneDeep(wireInfoValues) };

    wireInfoData.receivingBankInfo = { ...cloneDeep(receivingValues) };
    wireInfoData.beneficiaryBankInfo = { ...cloneDeep(beneficiaryValues) };
    wireInfoData.intermediaryBankInfo = { ...cloneDeep(intermediaryValues) };
    valuesInvestorDetail.wireInfo = { ...cloneDeep(wireInfoData) };

    if (isClientPortalUser) {
      valuesInvestorDetail.clientPortalWireInfo = {
        ...cloneDeep(wireInfoData),
      };
    }
    return valuesInvestorDetail;
  };

  const approveChanges = async () => {
    try {
      setSubmitingInvestor(true);
      await approvePendingChanges(
        selectedInvestorListItem?.pendingWireInfo?.id,
        selectedInvestorListItem?.id
      );
      setSubmitingInvestor(false);
      hidePanel();
      informationAlert(CHANGES_APPROVED_SUCCESS_MESSAGE, "success");
    } catch (err) {
      informationAlert(INVESTORS_NO_FUNDS_ERROR, "error");
    }
  };

  const declineChanges = async () => {
    try {
      setSubmitingInvestor(true);
      await declinePendingChanges(selectedInvestorListItem?.id);
      setSubmitingInvestor(false);
      hidePanel();
      informationAlert(CHANGES_DECLINE_SUCCESS_MESSAGE, "success");
    } catch (err) {
      informationAlert(INVESTORS_NO_FUNDS_ERROR, "error");
    }
  };

  const isPrimaryContact = selectedInvestorListItem?.isPrimary;
  const canUpdateDetails = isClientPortalUser
    ? isPrimaryContact
    : isSuperAdminOrClientAdmin;
  const canUpdateFund = isClientPortalUser ? false : isSuperAdminOrClientAdmin;
  const canUpdateWireInfo = isClientPortalUser
    ? isPrimaryContact
    : isSuperAdminOrClientAdmin;
  const canViewWireInfo = isClientPortalUser ? isPrimaryContact : true;
  const showActionButtons =
    canUpdateDetails || canUpdateFund || canUpdateWireInfo;
  const canUpdateInvestorTin = isClientPortalUser
    ? false
    : isSuperAdminOrClientAdmin;

  const isDirty = isTaxAddressDirty || isWireInfoDirty || isIntermediaryDirty || isInvestorDetailDirty || isBeneficiaryInfoDirty || isReceivingDirty || isMalingAddressDirty;

  useEffect(() => {
    setAppHasChanges(isDirty);
  }, [isDirty]);

  return (
    <React.Fragment key="right">
      <InvestorDetailPanel
        id="panel_investor_detail"
        anchor="right"
        open={isPanelVisible["right"]}
        onClose={handleOnClose}
      >
        <Box>
          <HorizontalBox>
            <Title id="title">
              {title} <ImgIcon icon={IconRightArrow} />
            </Title>
            <CloseIcon
              id="btn_investor_detail_close"
              color="primary"
              onClick={handleOnClose}
            />
          </HorizontalBox>
          <Box>
            <TabsPanel
              id={"investor_detail_panel_tabs"}
              value={currentTab}
              handleChange={handleTabChange}
              options={
                canViewWireInfo
                  ? ["Details", "Funds", "Wiring"]
                  : ["Details", "Funds"]
              }
            />
          </Box>
        </Box>

        <Grid container rowSpacing={1}>
          <Box>
            <LateralPanelBox>
              <div ref={detailsRef}>
                {canUpdateDetails ? (
                  <>
                    {isSuperAdminOrClientAdmin &&
                      selectedInvestorListItem?.pendingWireInfo && (
                        <InvestorPendingView
                          selectedInvestor={investor}
                          selectedInvestorListItem={selectedInvestorListItem}
                          approvePendingChanges={approveChanges}
                          declinePendingChanges={declineChanges}
                        />
                      )}
                    <InvestorDetails
                      setIsLoading={setIsLoading}
                      useInvestorInput={useInputInvestorDetail}
                      useCheckbox={useCheckboxInvestorDetail}
                      useAddressInput={useInputAddress}
                      useTaxAddressInput={useInputTaxAddress}
                      useTaxAddressInputSelect={useInputTaxAddressSelect}
                      addressValues={addressValues!}
                      taxAddressValues={taxAddressValues!}
                      valuesInvestorDetail={valuesInvestorDetail}
                      isUniqueNameError={isUniqueNameError}
                      setUniqueNameError={setUniqueNameError}
                      setRequiredFieldUpdated={setRequiredfieldUpdated}
                      canUpdateInvestorTin={canUpdateInvestorTin}
                      canUpdateInvestorName={canUpdateInvestorTin}
                    />
                  </>
                ) : (
                  <InvestorDetailsReadOnlyView
                    valuesInvestorDetail={valuesInvestorDetail}
                  />
                )}
              </div>

              <Divider variant="middle" />

              <div ref={fundsRef}>
                {canUpdateFund ? (
                  <InvestorFunds
                    investorFundList={
                      valuesInvestorDetail.funds
                        ? valuesInvestorDetail.funds
                        : (valuesInvestorDetail.funds = [])
                    }
                    setFundListUpdated={setRequiredfieldUpdated}
                  />
                ) : (
                  <InvestorFundsReadOnly
                    investorFundList={
                      valuesInvestorDetail.funds
                        ? valuesInvestorDetail.funds
                        : (valuesInvestorDetail.funds = [])
                    }
                  />
                )}
              </div>

              <Divider variant="middle" />
              {canViewWireInfo && (
                <>
                  {canUpdateWireInfo ? (
                    <div ref={wiringRef}>
                      <InvestorWire
                        useInvestorWireInput={useInputWireInfo}
                        useReceivingInput={useReceivingInput}
                        useBeneficiaryInput={useBeneficiaryInput}
                        useIntermediaryInput={useIntermediaryInput}
                        investorWireInfo={wireInfoValues}
                        scrollToBottom={scrollToBottom}
                      />
                    </div>
                  ) : (
                    <div ref={wiringRef}>
                      <InvestorWireReadOnly
                        investorReceivingInfo={defaultReceivingBankInfo}
                        investorBeneficiaryInfo={beneficiaryBankInfo}
                        investorIntermediaryInfo={intermediaryBankInfo}
                        investorWireInfo={wireInfoValues}
                      />
                    </div>
                  )}
                </>
              )}
              <div ref={pageBottomRef} />
            </LateralPanelBox>
          </Box>
        </Grid>

        {showActionButtons && (
          <HorizontalBox
            addTopShadow
            attachToBottom={true}
            hidden={false}
            fullWidth
          >
            <Button
              id={"btn_investor_detail_panel_cancel"}
              variant="outlined"
              onClick={handleOnClose}
              text={"Cancel"}
              color={"secondary"}
              fullWidth
              addSpaceBetweenButtons
            />

            <Button
              id={"btn_investor_detail_panel_save"}
              variant="contained"
              onClick={handleOnSave}
              text={"Save & Close"}
              color={"primary"}
              fullWidth
              addSpaceBetweenButtons
              disabled={!isFormValid}
            />
          </HorizontalBox>
        )}

        <StyledBackdrop
          open={
            isLoading || isLoadingInvestor || isSubmittingInvestor || isCheckingUniqueName
          }
        />
        <ProgressPanel
          id={"progress_investor_panel"}
          showProgress={
            isLoading || isLoadingInvestor || isSubmittingInvestor || isCheckingUniqueName
          }
        />
      </InvestorDetailPanel>

      <FullScreenModal
        id="dialog_investor_panel_leave_confirmation"
        open={showLeaveConfirmation}
        setOpen={setShowLeaveConfirmation}
        title={`You have unsaved changes`}
        subtitle={UNSAVED_CHANGES_SUBTITLE}
        confirmButtonText={TEXT_SAVE_CHANGES}
        cancelButtonText={TEXT_DISCARD_CHANGES}
        onSuccess={handleOnSave}
        onCancel={handleOnDiscard}
      />
    </React.Fragment>
  );
};

export default IndividualPanel;
